import { useEffect, useMemo, useState } from 'react';
import { X, Search, Flag, Crosshair, Loader2 } from 'lucide-react';
import { listObjectives, type StrategicObjective } from '../../../lib/strategyAlignment';
import { createStrategicObjective as createObjective } from '../../../lib/strategy/createStrategyFlow';
import {
  useDesiredOutcomeStore,
  type DesiredOutcome,
} from '../../../stores/prioritization/desiredOutcomeStore';

export type OstRootLinkMode = 'objective' | 'desired-outcome';

export function desiredOutcomeToRootFields(o: DesiredOutcome): { title: string; description: string } {
  const title = o.title?.trim() || o.whatWanted?.trim() || 'Desired outcome';
  const parts = [
    o.who?.trim() && `Who: ${o.who.trim()}`,
    o.whatWanted?.trim() && `What: ${o.whatWanted.trim()}`,
    o.deeperBenefit?.trim() && `Deeper benefit: ${o.deeperBenefit.trim()}`,
  ].filter(Boolean) as string[];
  return { title, description: parts.join('\n\n') };
}

interface OstRootLinkModalProps {
  mode: OstRootLinkMode;
  outcomeNodeId: string;
  workspaceId: string | null | undefined;
  strategyId: string | undefined;
  userId: string | null | undefined;
  onClose: () => void;
  onApplyObjective: (outcomeNodeId: string, objective: StrategicObjective) => void | Promise<void>;
  onApplyDesiredOutcome: (outcomeNodeId: string, outcome: DesiredOutcome) => void | Promise<void>;
}

export function OstRootLinkModal({
  mode,
  outcomeNodeId,
  workspaceId,
  strategyId,
  userId,
  onClose,
  onApplyObjective,
  onApplyDesiredOutcome,
}: OstRootLinkModalProps) {
  const [panel, setPanel] = useState<'pick' | 'create'>('pick');
  const [query, setQuery] = useState('');
  const [objectives, setObjectives] = useState<StrategicObjective[]>([]);
  const [loadingObjectives, setLoadingObjectives] = useState(mode === 'objective');

  const [newObjectiveName, setNewObjectiveName] = useState('');
  const [newObjectiveDescription, setNewObjectiveDescription] = useState('');
  const [newOutcomeTitle, setNewOutcomeTitle] = useState('');
  const [newOutcomeWho, setNewOutcomeWho] = useState('');
  const [newOutcomeWhat, setNewOutcomeWhat] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const outcomes = useDesiredOutcomeStore((s) => s.outcomes);
  const fetchOutcomes = useDesiredOutcomeStore((s) => s.fetchOutcomes);
  const createOutcome = useDesiredOutcomeStore((s) => s.createOutcome);
  const outcomesLoading = useDesiredOutcomeStore((s) => s.isLoading);

  useEffect(() => {
    if (mode === 'objective' && workspaceId) {
      setLoadingObjectives(true);
      listObjectives(workspaceId, strategyId)
        .then(setObjectives)
        .finally(() => setLoadingObjectives(false));
    }
  }, [mode, workspaceId, strategyId]);

  useEffect(() => {
    if (mode === 'desired-outcome') {
      fetchOutcomes();
    }
  }, [mode, fetchOutcomes]);

  const filteredObjectives = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return objectives;
    return objectives.filter(
      (o) =>
        o.name.toLowerCase().includes(q) || (o.description && o.description.toLowerCase().includes(q))
    );
  }, [objectives, query]);

  const filteredDesired = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return outcomes;
    return outcomes.filter(
      (o) =>
        o.title.toLowerCase().includes(q) ||
        o.whatWanted.toLowerCase().includes(q) ||
        (o.who && o.who.toLowerCase().includes(q))
    );
  }, [outcomes, query]);

  const title =
    mode === 'objective' ? 'Link a strategic objective' : 'Link a desired outcome';

  const subtitle =
    mode === 'objective'
      ? 'Replaces the strategy node title and description with this objective.'
      : 'Replaces the strategy node with the core fields from this desired outcome.';

  const handleCreateObjective = async () => {
    const name = newObjectiveName.trim();
    if (!name) {
      setCreateError('Name is required.');
      return;
    }
    if (!workspaceId) {
      setCreateError('Select a workspace first.');
      return;
    }
    if (!userId) {
      setCreateError('You must be signed in to create an objective.');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const created = await createObjective(
        {
          name,
          description: newObjectiveDescription.trim(),
          strategyId: strategyId || undefined,
          timeHorizon: 'quarterly',
          priority: 'high',
          status: 'active',
          workspaceId,
          createdBy: userId,
        },
        userId
      );
      setObjectives((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      await onApplyObjective(outcomeNodeId, created);
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : 'Failed to create objective');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateDesiredOutcome = async () => {
    const title = newOutcomeTitle.trim();
    if (!title) {
      setCreateError('Title is required.');
      return;
    }
    setCreating(true);
    setCreateError(null);
    const { outcome, error: err } = await createOutcome({
      title,
      who: newOutcomeWho.trim(),
      whatWanted: newOutcomeWhat.trim(),
    });
    setCreating(false);
    if (err || !outcome) {
      setCreateError(err || 'Failed to create desired outcome');
      return;
    }
    await onApplyDesiredOutcome(outcomeNodeId, outcome);
  };

  const inputCls =
    'w-full  border border-[var(--rule)] bg-[var(--bg-raised)] py-2 px-3 text-sm text-[var(--text-pri)] placeholder-gray-400 focus:border-[var(--slate)] focus:outline-none focus:ring-2 focus:ring-indigo-100 dark:bg-[var(--bg)]/40 dark:text-[var(--text-pri)]';

  const segmentBtn = (active: boolean) =>
    `flex-1  px-3 py-1.5 text-xs font-medium transition-colors ${
      active
        ? 'bg-[var(--bg-raised)] text-[var(--text-pri)] shadow-sm dark:text-[var(--text-pri)]'
        : 'text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:text-[var(--text-ter)] dark:hover:text-[var(--text-sec)]'
    }`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        aria-label="Close"
        onClick={onClose}
      />
      <div
        className="relative max-h-[90vh] w-full max-w-md overflow-y-auto border border-[var(--rule)]/80 bg-[var(--bg-raised)] shadow-2xl dark:bg-[var(--surface)]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ost-root-link-title"
      >
        <div className="flex items-start justify-between gap-3 border-b border-[var(--rule)] px-5 py-4">
          <div>
            <h3 id="ost-root-link-title" className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              {title}
            </h3>
            <p className="mt-1 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">{subtitle}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-[var(--text-ter)] hover:bg-[var(--hover-tint)] hover:text-[var(--text-sec)] dark:hover:bg-[var(--hover-tint)]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 py-3 space-y-3">
          <div className="flex border border-[var(--rule)] bg-[var(--surface)] p-0.5 dark:bg-[var(--bg)]/50">
            <button
              type="button"
              className={segmentBtn(panel === 'pick')}
              onClick={() => {
                setCreateError(null);
                setPanel('pick');
              }}
            >
              Link existing
            </button>
            <button
              type="button"
              className={segmentBtn(panel === 'create')}
              onClick={() => {
                setCreateError(null);
                setPanel('create');
              }}
            >
              Create new
            </button>
          </div>

          {panel === 'pick' && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--text-ter)]" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={mode === 'objective' ? 'Search objectives…' : 'Search desired outcomes…'}
                className="w-full border border-[var(--rule)] bg-[var(--bg-raised)] py-2 pl-9 pr-3 text-sm text-[var(--text-pri)] placeholder-gray-400 focus:border-[var(--slate)] focus:outline-none focus:ring-2 focus:ring-indigo-100 dark:bg-[var(--bg)]/40 dark:text-[var(--text-pri)]"
              />
            </div>
          )}

          {panel === 'create' && createError && (
            <div className="border border-[var(--coral)] bg-[var(--coral-dim)] px-3 py-2 text-xs text-[var(--coral)] dark:border-[var(--coral)] dark:bg-[var(--coral)]/40 dark:text-[var(--coral)]">
              {createError}
            </div>
          )}

          {panel === 'create' && mode === 'objective' && (
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                  Name <span className="text-[var(--coral)]">*</span>
                </label>
                <input
                  type="text"
                  value={newObjectiveName}
                  onChange={(e) => setNewObjectiveName(e.target.value)}
                  placeholder="e.g., Improve onboarding completion"
                  className={inputCls}
                  disabled={creating}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                  Description
                </label>
                <textarea
                  value={newObjectiveDescription}
                  onChange={(e) => setNewObjectiveDescription(e.target.value)}
                  placeholder="Optional — what success looks like"
                  rows={2}
                  className={`${inputCls} resize-none`}
                  disabled={creating}
                />
              </div>
              <button
                type="button"
                onClick={handleCreateObjective}
                disabled={creating || !newObjectiveName.trim()}
                className="w-full bg-consulting-slate-blue-600 py-2 text-sm font-medium text-white hover:bg-consulting-slate-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {creating ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Creating…
                  </span>
                ) : (
                  'Create and link'
                )}
              </button>
            </div>
          )}

          {panel === 'create' && mode === 'desired-outcome' && (
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                  Title <span className="text-[var(--coral)]">*</span>
                </label>
                <input
                  type="text"
                  value={newOutcomeTitle}
                  onChange={(e) => setNewOutcomeTitle(e.target.value)}
                  placeholder="Short name for this outcome"
                  className={inputCls}
                  disabled={creating}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                  Who wants it
                </label>
                <input
                  type="text"
                  value={newOutcomeWho}
                  onChange={(e) => setNewOutcomeWho(e.target.value)}
                  placeholder="Optional"
                  className={inputCls}
                  disabled={creating}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                  What&apos;s wanted
                </label>
                <input
                  type="text"
                  value={newOutcomeWhat}
                  onChange={(e) => setNewOutcomeWhat(e.target.value)}
                  placeholder="Optional — one positive sentence"
                  className={inputCls}
                  disabled={creating}
                />
              </div>
              <button
                type="button"
                onClick={handleCreateDesiredOutcome}
                disabled={creating || !newOutcomeTitle.trim()}
                className="w-full bg-consulting-slate-blue-600 py-2 text-sm font-medium text-white hover:bg-consulting-slate-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {creating ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Creating…
                  </span>
                ) : (
                  'Create and link'
                )}
              </button>
            </div>
          )}
        </div>

        <div className="max-h-64 overflow-y-auto border-t border-[var(--rule)]">
          {panel === 'create' && (
            <p className="px-5 py-6 text-center text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
              Switch to &ldquo;Link existing&rdquo; to search and pick from your workspace.
            </p>
          )}
          {panel === 'pick' && mode === 'objective' && loadingObjectives && (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-[var(--text-ter)]">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading objectives…
            </div>
          )}
          {panel === 'pick' && mode === 'objective' && !loadingObjectives && filteredObjectives.length === 0 && (
            <p className="px-5 py-8 text-center text-sm text-[var(--text-ter)]">
              No objectives match. Try another search, or use Create new above.
            </p>
          )}
          {panel === 'pick' &&
            mode === 'objective' &&
            !loadingObjectives &&
            filteredObjectives.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => onApplyObjective(outcomeNodeId, o)}
                className="flex w-full items-start gap-3 border-b border-[var(--rule)] px-5 py-3 text-left last:border-b-0 hover:bg-consulting-slate-blue-50/60 dark:hover:bg-consulting-slate-blue-900/15"
              >
                <Flag className="mt-0.5 h-4 w-4 flex-shrink-0 text-consulting-slate-blue-600" />
                <span>
                  <span className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                    {o.name}
                  </span>
                  {o.description && (
                    <span className="mt-0.5 line-clamp-2 block text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                      {o.description}
                    </span>
                  )}
                </span>
              </button>
            ))}

          {panel === 'pick' && mode === 'desired-outcome' && outcomesLoading && (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-[var(--text-ter)]">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading desired outcomes…
            </div>
          )}
          {panel === 'pick' && mode === 'desired-outcome' && !outcomesLoading && filteredDesired.length === 0 && (
            <p className="px-5 py-8 text-center text-sm text-[var(--text-ter)]">
              No desired outcomes yet. Use Create new above, or add more detail on the Desired Outcomes page later.
            </p>
          )}
          {panel === 'pick' &&
            mode === 'desired-outcome' &&
            !outcomesLoading &&
            filteredDesired.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => onApplyDesiredOutcome(outcomeNodeId, o)}
                className="flex w-full items-start gap-3 border-b border-[var(--rule)] px-5 py-3 text-left last:border-b-0 hover:bg-consulting-slate-blue-50/60 dark:hover:bg-consulting-slate-blue-900/15"
              >
                <Crosshair className="mt-0.5 h-4 w-4 flex-shrink-0 text-consulting-slate-blue-600" />
                <span>
                  <span className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                    {o.title || o.whatWanted || 'Untitled'}
                  </span>
                  {o.whatWanted && o.title !== o.whatWanted && (
                    <span className="mt-0.5 line-clamp-2 block text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                      {o.whatWanted}
                    </span>
                  )}
                </span>
              </button>
            ))}
        </div>

      </div>
    </div>
  );
}
